import React, { useState, useEffect } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { fileService } from '../services/fileService';

const FileViewer = ({ file }) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFile = async () => {
      if (!file) return;

      try {
        setLoading(true);
        setError(null);
        
        // Get signed URL for the file
        const url = await fileService.getFileUrl(file.file_path);
        setFileUrl(url);
      } catch (err) {
        console.error('Error loading file:', err);
        setError('Failed to load file');
      } finally {
        setLoading(false);
      }
    };

    loadFile();
  }, [file]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading file...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!file || !fileUrl) {
    return <Alert variant="warning">No file to display</Alert>;
  }

  const fileType = file.file_type || '';
  const fileName = file.original_name || '';
  const fileExtension = fileName.split('.').pop().toLowerCase();

  // PDF files
  if (fileType === 'application/pdf' || fileExtension === 'pdf') {
    return (
      <div style={{ width: '100%', height: '600px' }}>
        <iframe
          src={fileUrl}
          width="100%"
          height="100%"
          style={{ border: 'none' }}
          title={fileName}
        />
      </div>
    );
  }

  // Image files
  if (fileType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
    return (
      <div className="text-center">
        <img
          src={fileUrl}
          alt={fileName}
          style={{ 
            maxWidth: '100%', 
            maxHeight: '600px',
            objectFit: 'contain'
          }}
          className="img-fluid"
        />
      </div>
    );
  }

  // Excel files
  if (fileType.includes('spreadsheet') || ['xlsx', 'xls', 'ods'].includes(fileExtension)) {
    return (
      <Alert variant="info" className="text-center">
        <h5>📊 Excel File</h5>
        <p>Excel files cannot be previewed directly. Please download to view.</p>
        <p><strong>File:</strong> {fileName}</p>
        <p><strong>Size:</strong> {formatFileSize(file.file_size)}</p>
      </Alert>
    );
  }

  // Email files
  if (fileType.includes('message') || ['msg', 'eml'].includes(fileExtension)) {
    return (
      <Alert variant="info" className="text-center">
        <h5>📧 Email File</h5>
        <p>Email files cannot be previewed directly. Please download to view.</p>
        <p><strong>File:</strong> {fileName}</p>
        <p><strong>Size:</strong> {formatFileSize(file.file_size)}</p>
      </Alert>
    );
  }

  // Text files
  if (fileType.startsWith('text/') || ['txt', 'csv', 'json', 'xml'].includes(fileExtension)) {
    return (
      <div style={{ width: '100%', height: '400px' }}>
        <iframe
          src={fileUrl}
          width="100%"
          height="100%"
          style={{ border: '1px solid #ddd' }}
          title={fileName}
        />
      </div>
    );
  }

  // Default fallback for unsupported files
  return (
    <Alert variant="warning" className="text-center">
      <h5>📄 File Preview Not Available</h5>
      <p>This file type cannot be previewed directly. Please download to view.</p>
      <p><strong>File:</strong> {fileName}</p>
      <p><strong>Type:</strong> {fileType}</p>
      <p><strong>Size:</strong> {formatFileSize(file.file_size)}</p>
    </Alert>
  );
};

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
};

export default FileViewer;
